export interface StandingTeam {
  id: string;
  name: string;
}

export interface StandingMatchInput {
  team1Id: string;
  team2Id: string;
  score1: number;
  score2: number;
}

export interface StandingCell {
  scoreFor: number;
  scoreAgainst: number;
  points: number;
}

export interface StandingRow {
  team: StandingTeam;
  /** Cell values aligned to `columns` order. */
  cells: Array<StandingCell | null | 'self'>;
  points: number;
  rank: number;
  pointDiff: number;
  pointsScored: number;
  pointsAgainst: number;
}

export interface StandingsTableData {
  columns: StandingTeam[];
  rows: StandingRow[];
}

/** Win = 2, loss = 1 (common HK amateur summer-league prelim scoring). */
export function matchPoints(scoreFor: number, scoreAgainst: number): number {
  if (scoreFor > scoreAgainst) return 2;
  if (scoreFor < scoreAgainst) return 1;
  return 1;
}

function headToHeadCompare(a: StandingRow, b: StandingRow, columns: StandingTeam[]): number | null {
  const bIndex = columns.findIndex((team) => team.id === b.team.id);
  if (bIndex < 0) return null;
  const cell = a.cells[bIndex];
  if (!cell || cell === 'self') return null;
  if (cell.scoreFor === cell.scoreAgainst) return null;
  // Negative => a before b (a won the head-to-head).
  return cell.scoreAgainst - cell.scoreFor;
}

/**
 * Ranking rules when 積分 are equal:
 * - Exactly 2 teams: head-to-head result decides.
 * - More than 2 teams: higher (得分 - 失分), then higher 得分.
 */
export function compareStandingRows(
  a: StandingRow,
  b: StandingRow,
  columns: StandingTeam[],
  teamsOnPoints: Map<number, number>
): number {
  if (b.points !== a.points) return b.points - a.points;

  const tiedCount = teamsOnPoints.get(a.points) ?? 0;

  if (tiedCount === 2) {
    const h2h = headToHeadCompare(a, b, columns);
    if (h2h !== null) return h2h;
  }

  if (tiedCount > 2 || tiedCount === 2) {
    if (b.pointDiff !== a.pointDiff) return b.pointDiff - a.pointDiff;
    if (b.pointsScored !== a.pointsScored) return b.pointsScored - a.pointsScored;
  }

  return a.team.name.localeCompare(b.team.name, 'zh-Hant');
}

export function buildStandings(
  teams: StandingTeam[],
  matches: StandingMatchInput[]
): StandingsTableData {
  const columns = [...teams].sort((a, b) =>
    a.name.localeCompare(b.name, 'zh-Hant')
  );
  const indexById = new Map(columns.map((team, index) => [team.id, index]));
  const n = columns.length;

  const matrix: Array<Array<StandingCell | null | 'self'>> = Array.from(
    { length: n },
    (_, row) =>
      Array.from({ length: n }, (_, col) => (row === col ? 'self' : null))
  );

  const totals = columns.map(() => ({
    points: 0,
    pointDiff: 0,
    pointsScored: 0,
    pointsAgainst: 0,
  }));

  for (const match of matches) {
    const i = indexById.get(match.team1Id);
    const j = indexById.get(match.team2Id);
    if (i === undefined || j === undefined || i === j) continue;

    const p1 = matchPoints(match.score1, match.score2);
    const p2 = matchPoints(match.score2, match.score1);

    matrix[i][j] = {
      scoreFor: match.score1,
      scoreAgainst: match.score2,
      points: p1,
    };
    matrix[j][i] = {
      scoreFor: match.score2,
      scoreAgainst: match.score1,
      points: p2,
    };

    totals[i].points += p1;
    totals[j].points += p2;
    totals[i].pointDiff += match.score1 - match.score2;
    totals[j].pointDiff += match.score2 - match.score1;
    totals[i].pointsScored += match.score1;
    totals[j].pointsScored += match.score2;
    totals[i].pointsAgainst += match.score2;
    totals[j].pointsAgainst += match.score1;
  }

  const unsorted: StandingRow[] = columns.map((team, index) => ({
    team,
    cells: matrix[index],
    points: totals[index].points,
    pointDiff: totals[index].pointDiff,
    pointsScored: totals[index].pointsScored,
    pointsAgainst: totals[index].pointsAgainst,
    rank: 0,
  }));

  const teamsOnPoints = new Map<number, number>();
  for (const row of unsorted) {
    teamsOnPoints.set(row.points, (teamsOnPoints.get(row.points) ?? 0) + 1);
  }

  const rows = [...unsorted].sort((a, b) =>
    compareStandingRows(a, b, columns, teamsOnPoints)
  );

  rows.forEach((row, index) => {
    if (
      index > 0 &&
      compareStandingRows(row, rows[index - 1], columns, teamsOnPoints) === 0
    ) {
      row.rank = rows[index - 1].rank;
    } else {
      row.rank = index + 1;
    }
  });

  return { columns, rows };
}
